/* Cellar heat-load model.
 *
 * This REPLACES the calibrated shortcut in wcc-quote.js btuEstimate(), which
 * was tuned to reproduce our published chart (100->1500, 1000->5500 BTU/hr)
 * rather than to compute a load. That chart and these numbers will not agree,
 * and the chart is the one that is wrong: it bundles infiltration, product
 * load and headroom into a single volume lookup.
 *
 * What this does instead, all standard HVAC practice:
 *   conduction   Q = SUM(area * dT / R) over ceiling, floor and walls
 *   infiltration sensible  1.08 * CFM * dT,  CFM = volume * ACH / 60
 *   infiltration latent    0.68 * CFM * d(grains/lb)
 *   lighting     watts * 3.412
 *   occupancy    people * (250 sensible + 200 latent)
 *   pull-down    bottles * 1.725 * dT / hours
 *
 * None of it was recovered from any third party. Constants are documented at
 * each use. Surfaces colder than the cellar contribute zero rather than a
 * negative, which keeps the result conservative.
 *
 * It deliberately does NOT choose equipment. Product selection stays on the
 * published wcc.capacity_cu_ft capacity in wcc-quote.js, because a
 * manufacturer rating is a claim we can stand behind and a computed load is
 * our own estimate.
 */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) module.exports = factory();
  else root.WCCLoad = factory();
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var K_SENS = 1.08;        // BTU/hr per CFM per degF, sea level
  var K_LAT = 0.68;         // BTU/hr per CFM per grain/lb
  var W_TO_BTU = 3.412142;
  var P_SENS = 250;         // per person, light activity or standing
  var P_LAT = 200;
  var BOTTLE_C = 1.725;     // BTU per degF per 750ml bottle: 1.65 lb wine at
                            // c=0.9 plus 1.2 lb glass at c=0.2
  var LIGHT_WFT = 0.75;     // W/ft2, LED cellar lighting
  var FT2_PER_PERSON = 100;

  function num(v, dflt) {
    var n = parseFloat(v);
    return isFinite(n) ? n : (dflt === undefined ? 0 : dflt);
  }

  /* Magnus saturation pressure -> humidity ratio -> grains per lb dry air. */
  function grains(tF, rhPct) {
    var tC = (tF - 32) * 5 / 9;
    var pws = 0.61094 * Math.exp(17.625 * tC / (tC + 243.04)); // kPa
    var pw = Math.max(0, Math.min(1, rhPct / 100)) * pws;
    var P = 101.325;
    if (pw >= P) return NaN;
    return 0.621945 * pw / (P - pw) * 7000;
  }

  function surface(name, areaFt2, rImp, dT) {
    if (!(rImp > 0) || !(areaFt2 > 0)) return null;
    var dt = dT < 0 ? 0 : dT;
    return { name: name, area: areaFt2, r: rImp, dt: dt, q: areaFt2 * dt / rImp };
  }

  /* input:
   *   length, width, height      ft
   *   cellarTemp                 degF, the target
   *   ambient                    degF, the exhaust-side room
   *   rWall, rCeiling, rFloor    imperial R. rWall may arrive as a U-value
   *                              from the existing select, so pass rWall as R.
   *   ach, lightWatts, people    optional, defaulted from the room
   *   rhOut, rhIn                %
   *   bottles, bottleTemp, bottleHours   optional pull-down
   *   glass, vapor, stdDoor      construction flags, applied to conduction
   */
  function compute(input) {
    var L = num(input.length), W = num(input.width), H = num(input.height);
    var cellar = num(input.cellarTemp, 55);
    var ambient = num(input.ambient, 75);
    var plan = L * W;
    var volume = plan * H;
    var dT = ambient - cellar;

    var rWall = num(input.rWall, 19);
    var rCeil = num(input.rCeiling, rWall);
    var rFloor = num(input.rFloor, 10);

    var rows = [];
    rows.push(surface('Ceiling', plan, rCeil, dT));
    rows.push(surface('Floor', plan, rFloor, dT));
    rows.push(surface('Walls', 2 * (L + W) * H, rWall, dT));
    rows = rows.filter(Boolean);

    var conduction = rows.reduce(function (s, r) { return s + r.q; }, 0);

    /* Construction penalties apply to conduction only. A glass wall is a
     * conduction path, not an air-change or a lighting load. These are the
     * same factors the capacity sizing uses, kept in step on purpose. */
    var adj = [];
    if (input.glass) { conduction *= 1.35; adj.push('glass wall or door, +35%'); }
    if (input.vapor === false) { conduction *= 1.15; adj.push('no vapor barrier, +15%'); }
    if (input.stdDoor) { conduction *= 1.10; adj.push('uninsulated door, +10%'); }

    var other = [];

    var ach = num(input.ach, 0.4);
    var cfm = volume * ach / 60;
    if (cfm > 0) {
      var dTi = dT < 0 ? 0 : dT;
      other.push({ name: 'Infiltration, sensible', q: K_SENS * cfm * dTi,
                   meta: cfm.toFixed(1) + ' CFM at ' + ach + ' ACH' });
      var gOut = grains(ambient, num(input.rhOut, 50));
      var gIn = grains(cellar, num(input.rhIn, 60));
      if (isFinite(gOut) && isFinite(gIn)) {
        var dg = Math.max(0, gOut - gIn);
        other.push({ name: 'Infiltration, latent', q: K_LAT * cfm * dg,
                     meta: dg.toFixed(1) + ' gr/lb' });
      }
    }

    var watts = input.lightWatts === undefined || input.lightWatts === null || input.lightWatts === ''
      ? Math.round(plan * LIGHT_WFT) : num(input.lightWatts);
    if (watts > 0) other.push({ name: 'Lighting', q: watts * W_TO_BTU, meta: watts + ' W' });

    var people = input.people === undefined || input.people === null || input.people === ''
      ? Math.max(1, Math.round(plan / FT2_PER_PERSON)) : num(input.people);
    if (people > 0) other.push({ name: 'Occupancy', q: people * (P_SENS + P_LAT),
                                 meta: people + ' x ' + (P_SENS + P_LAT) + ' BTU/hr' });

    var bottles = num(input.bottles);
    if (input.pullDown && bottles > 0) {
      var hrs = Math.max(1, num(input.bottleHours, 48));
      var dTb = Math.max(0, num(input.bottleTemp, ambient) - cellar);
      other.push({ name: 'Bottle pull-down', q: bottles * BOTTLE_C * dTb / hrs,
                   meta: bottles + ' bottles over ' + hrs + ' h' });
    }

    var otherTotal = other.reduce(function (s, r) { return s + r.q; }, 0);
    var total = conduction + otherTotal;

    return {
      volume: Math.round(volume),
      conduction: Math.round(conduction),
      conductionRows: rows,
      other: other,
      otherTotal: Math.round(otherTotal),
      total: Math.round(total),
      watts: Math.round(total / W_TO_BTU),
      adjustments: adj,
      deltaT: dT
    };
  }

  return { compute: compute, grains: grains, constants: {
    K_SENS: K_SENS, K_LAT: K_LAT, P_SENS: P_SENS, P_LAT: P_LAT,
    BOTTLE_C: BOTTLE_C, LIGHT_WFT: LIGHT_WFT, FT2_PER_PERSON: FT2_PER_PERSON
  } };
});
